import type { Control, ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { RichTextEditor } from '@/components/ui/rich-text-editor';

interface FormRichTextFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  description?: React.ReactNode;
  required?: boolean;
  className?: string;
  mentions?: boolean;
  labelActions?: (
    field: ControllerRenderProps<TFieldValues, FieldPath<TFieldValues>>
  ) => React.ReactNode;
}

export const FormRichTextField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  required,
  className,
  mentions = false,
  labelActions,
}: FormRichTextFieldProps<TFieldValues>) => {
  const { communityId } = useParams<{ communityId?: string }>();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <FormLabel>
              {label} {required && <span className="text-primary">*</span>}
            </FormLabel>
            {labelActions?.(field)}
          </div>
          <FormControl>
            <RichTextEditor
              value={(field.value as string) || ''}
              onChange={field.onChange}
              communityId={mentions ? communityId : undefined}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
FormRichTextField.displayName = 'FormRichTextField';
